import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { AlertTriangle, X } from 'lucide-react';
import { deleteCreditCard } from '../../store/thunk/userThunk';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { updateUserCCList } from '../../store/reducer/authSlice';
import { selectAuthLoading } from '../../store/reducer/authSlice';
import { fetchUserPayments } from '../../store/thunk/authThunk';
import ButtonLoader from '../../shared/loader/ButtonLoader';

const DeleteCreditCard = ({ userInfo, deleteCard, open, setOpen }) => {
    const dispatch = useDispatch();
    const loading = useSelector(selectAuthLoading);

    const handleClose = () => {
        if (!loading) {
            setOpen(false);
        }
    };

    const handleDeleteCard = (id) => {
        if (!id) {
            toast.error('Invalid card ID');
            return;
        }

        const payload = {
            user_id: userInfo?.user_id,
            payment_card_id: id,
            action: 'delete-credit-card',
        };

        const deleteCardPromise = dispatch(deleteCreditCard(payload)).unwrap();

        toast.promise(
            deleteCardPromise.then(async (resultAction) => {
                if (resultAction.status === 0) {
                    throw new Error(resultAction.message || 'Failed to delete card.');
                }

                // Success - update the card list
                dispatch(updateUserCCList(resultAction?.user_all_cards || []));

                // Refresh user payments
                await dispatch(fetchUserPayments());

                // Close modal
                setOpen(false);

                return resultAction;
            }),
            {
                loading: 'Deleting card...',
                success: (res) => res.message || 'Payment method deleted successfully!',
                error: (err) => err.message || 'Failed to delete card. Please try again.',
            },
            {
                success: {
                    duration: 3000,
                },
                error: {
                    duration: 4000,
                },
            },
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} className='relative z-50'>
            <DialogBackdrop className='fixed inset-0 bg-black/50' />
            <div className='fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto'>
                <DialogPanel className='bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto'>
                    <div className='relative p-6 pb-4'>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className='absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            <X size={20} />
                        </button>

                        {/* Warning Icon */}
                        <div className='flex justify-center mb-4'>
                            <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center'>
                                <AlertTriangle size={32} className='text-red-600' />
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className='text-2xl letter-spacing-1 font-bold text-gray-900 text-center mb-3 leading-tight'>
                            Delete Payment Card?
                        </h1>

                        {/* Subtitle */}
                        <p className='text-gray-600 letter-spacing-07 text-center text-xs md:text-sm mb-6 leading-relaxed'>
                            Your payment card will be permanently removed from your account. This
                            action cannot be undone.
                        </p>
                    </div>

                    {/* Card Preview */}
                    <div className='px-6 mb-6'>
                        <div className='bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 shadow-sm'>
                            <div className='flex items-center gap-4'>
                                {/* Card Visual */}
                                <div>
                                    <img
                                        className='w-14 h-9'
                                        src={`https://images.halalfull.com/assets/img/icon/cc/${
                                            deleteCard?.card_type || 'visa'
                                        }.svg`}
                                        alt={`${deleteCard?.card_type || 'Card'} Icon`}
                                        onError={(e) => {
                                            e.target.src =
                                                'https://images.halalfull.com/assets/img/icon/cc/visa.svg';
                                        }}
                                    />
                                </div>

                                {/* Card Details */}
                                <div className='flex-1'>
                                    <div className='flex items-center gap-2 mb-1'>
                                        <span className='text-md font-semibold letter-spacing-1 text-gray-900 capitalize'>
                                            {deleteCard?.card_holder_name || 'Card Holder'}
                                        </span>
                                    </div>
                                    <div className='text-sm letter-spacing-1 text-gray-600'>
                                        •••• ••••
                                        <span className='font-bold text-gray-900 ml-1'>
                                            {deleteCard?.card_number || '****'}
                                        </span>
                                        <span className='text-gray-600 ml-1'>
                                            | Exp: {deleteCard?.card_expiry_date || 'MM/YY'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className='px-6 mb-4'>
                            <div className='flex items-center justify-center gap-2 text-gray-600'>
                                <ButtonLoader />
                                <span className='text-sm'>Deleting payment method...</span>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className='px-6 pb-6'>
                        <div className='flex gap-3'>
                            <button
                                className='flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                className='flex-1 px-4 py-3 border border-red-500 text-red-500 font-medium rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-300 shadow-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                onClick={() => handleDeleteCard(deleteCard?.payment_id)}
                                disabled={loading}
                            >
                                {loading ? <ButtonLoader /> : 'Delete Card'}
                            </button>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
};

export default DeleteCreditCard;
